"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import Image from "next/image";
import { cn } from "@/lib/utils";

const CTA_BUTTON_CLASS =
	"inline-flex items-center hover:text-white justify-center rounded bg-themePrimary400 px-8 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-90 md:px-10 md:py-4 md:text-base";

/** 未传三图时的底图（走 next/image 优化，替代 CSS background-image） */
const DEFAULT_CTA_BACKGROUND = "/image/new-version/machining/cta-background.jpg" as const;

export interface CTAProps {
	/** 翻译命名空间，默认 fiveAxisCTA */
	translationNamespace?: string;
	/** 按钮跳转链接，若以 http 开头则用外链打开 */
	buttonHref?: string;
	/** 可选：三张横幅图路径，传入时在区块内展示三图横排，不传则使用默认背景图 */
	images?: [string, string, string];
	className?: string;
}

export default function CTA({
	translationNamespace = "fiveAxisCTA",
	buttonHref = "/upload",
	images,
	className,
}: CTAProps) {
	const t = useTranslations(translationNamespace);
	const isExternal = buttonHref.startsWith("http");
	const hasImages = images && images.length === 3;

	return (
		<section
			className={cn(
				"relative w-full overflow-hidden py-8 sm:py-10 md:py-12 lg:py-16",
				hasImages && "bg-[#e3e2e2]",
				className,
			)}
		>
			{!hasImages && (
				<div className="pointer-events-none absolute inset-0 z-0">
					<Image
						src={DEFAULT_CTA_BACKGROUND}
						alt=""
						fill
						className="object-cover object-center"
						sizes="100vw"
					/>
				</div>
			)}
			{/* 内容区域 */}
			<div className="container relative z-10">
				<div className="relative flex flex-col items-center justify-center text-center">
					{hasImages && (
						<>
							<div className="pointer-events-none absolute bottom-0 left-0 z-0 h-[112px] w-[112px] sm:h-[136px] sm:w-[136px] md:h-[168px] md:w-[168px] lg:h-[196px] lg:w-[196px] lg:-translate-x-6 xl:-translate-x-10">
								<Image
									src={images[0]}
									alt=""
									fill
									className="object-contain object-bottom"
									sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
								/>
							</div>
							<div className="pointer-events-none absolute bottom-0 right-[22%] z-0 h-[112px] w-[112px] translate-y-6 sm:h-[136px] sm:w-[136px] sm:translate-y-8 md:h-[168px] md:w-[168px] md:translate-y-10 lg:h-[196px] lg:w-[196px] lg:translate-y-12 xl:right-[24%]">
								<Image
									src={images[1]}
									alt=""
									fill
									className="object-contain object-bottom"
									sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
								/>
							</div>
							<div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[112px] w-[112px] sm:h-[136px] sm:w-[136px] md:h-[168px] md:w-[168px] lg:h-[196px] lg:w-[196px] lg:translate-x-4 xl:translate-x-8">
								<Image
									src={images[2]}
									alt=""
									fill
									className="object-contain object-bottom"
									sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
								/>
							</div>
						</>
					)}
					{/* 标题 - 两行文案，装饰线在第一行右侧，与文字底沿对齐 */}
					<div className="relative z-10 mb-8">
						<div className="flex flex-wrap items-end justify-center gap-x-3 gap-y-1 sm:gap-x-4">
							<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								{t("titleLine1")}
							</span>
							<span
								className="mb-[0.6em] h-[2px] w-12 shrink-0 bg-themePrimary400 sm:w-16 md:w-20 lg:w-24"
								aria-hidden
							/>
						</div>
						<h2 className="mt-2 text-2xl font-bold leading-tight text-black sm:mt-3 sm:text-[30px] md:text-4xl">
							{t("titleLine2")}
						</h2>
					</div>

					{/* CTA 按钮 */}
					{isExternal ? (
						<a href={buttonHref} target="_blank" rel="noopener noreferrer" className={CTA_BUTTON_CLASS}>
							{t("buttonText")}
						</a>
					) : (
						<Link href={buttonHref} className="relative z-10 inline-flex items-center hover:text-white justify-center rounded bg-themePrimary400 px-8 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-90 md:px-10 md:py-4 md:text-base">
							{t("buttonText")}
						</Link>
					)}
				</div>
			</div>
		</section>
	);
}
